import { useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import CloudUploadRounded from '@mui/icons-material/CloudUploadRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import ViewInArRounded from '@mui/icons-material/ViewInArRounded';
import { uploadModel } from '../../services/uploadsApi';

const formatBytes = (bytes) => {
  if (!bytes || Number.isNaN(Number(bytes))) return '';
  const mb = Number(bytes) / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  const kb = Number(bytes) / 1024;
  return `${Math.max(1, Math.round(kb))} KB`;
};

// Lists the .glb 3D models attached to a product (or to a per-store override
// of that product) and lets the user upload more or remove existing entries.
// The `value` shape is an array of { url, filename, content_type, size }.
// Read-only mode is used inside StoreTabPanel when a store tab is set to
// "Use default value for this store" — in that case the panel passes
// readOnly=true and onChange is a no-op.
const ThreeDModelsEditor = ({ value, onChange, readOnly = false }) => {
  const list = Array.isArray(value) ? value : [];
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');

  // Allowed extensions parallel the server (uploadsController.MODEL_EXTENSIONS).
  // Server is authoritative — reject early on the client for fast feedback.
  const ALLOWED_EXTS = ['.glb', '.gltf', '.obj', '.fbx', '.stl', '.zip'];
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const lower = file.name.toLowerCase();
    if (!ALLOWED_EXTS.some((ext) => lower.endsWith(ext))) {
      setError(`Unsupported file type. Allowed: ${ALLOWED_EXTS.join(', ')}`);
      return;
    }
    setError('');
    setUploading(true);
    setProgress(0);
    try {
      const record = await uploadModel(file, 'product-3d', setProgress);
      onChange([...list, record]);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Upload failed');
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const removeAt = (idx) => onChange(list.filter((_, i) => i !== idx));

  return (
    <Box>
      <input
        ref={inputRef}
        type="file"
        accept=".glb,.gltf,.obj,.fbx,.stl,.zip,model/gltf-binary,model/gltf+json,model/obj,model/stl,application/zip"
        style={{ display: 'none' }}
        onChange={handleFile}
      />

      {list.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          No 3D models uploaded yet.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.5 }}>
          {list.map((m, idx) => (
            <Box
              key={`${m.url}-${idx}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.5,
                px: 1.5,
                py: 1,
              }}
            >
              <ViewInArRounded fontSize="small" sx={{ color: 'primary.main' }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Tooltip title={m.url}>
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <a href={m.url} target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>
                      {m.filename || m.url.split('/').pop()}
                    </a>
                  </Typography>
                </Tooltip>
                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                  {m.content_type || 'model/gltf-binary'}{m.size ? ` · ${formatBytes(m.size)}` : ''}
                </Typography>
              </Box>
              {!readOnly && (
                <IconButton size="small" onClick={() => removeAt(idx)} title="Remove">
                  <CloseRounded sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </Box>
          ))}
        </Box>
      )}

      {!readOnly && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={uploading ? <CircularProgress size={14} /> : <CloudUploadRounded />}
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? (progress > 0 ? `Uploading… ${progress}%` : 'Uploading…') : 'Upload 3D file'}
          </Button>
          {error && (
            <Typography variant="caption" color="error">{error}</Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ThreeDModelsEditor;
